import type { CSSProperties } from "react";
import styled from "styled-components";

import { Spinner } from "../Spinner";
import { useVideoTimeline, type PlaybackTransport } from "../Video";
import { AnnotationContourLayer } from "./AnnotationContourLayer";
import { AnnotationRenderResourceImage, MeasuredImage } from "./AnnotationRenderResourceImage";
import {
  AnnotationOverlay,
  AnnotationViewport,
  CapturedVideoSurface,
  EmptyRaster,
  Raster,
  ViewportOverlayActions,
  annotationStageProps,
} from "./AnnotationViewportPrimitives";
import {
  useAnnotationRasterStage,
  useVideoRaster,
  type RasterReporter,
} from "./annotationRasterStage";
import { targetColorCss } from "./targetColor";
import type {
  AnnotationStudioController,
  AnnotationTarget,
  AnnotationTrackingPreviewState,
} from "./types";
import {
  useTrackingPreviewDisplayFrame,
  type TrackingPreviewPlayback,
} from "./useTrackingPreviewPlayback";
import { useTrackingFrameRenderResource } from "./useTrackingFrameRenderResource";

const TrackingOverlay = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.45rem;
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: ${({ theme }) => theme.color.bg.surfaceOverlay};
  color: ${({ theme }) => theme.color.text.default};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-variant-numeric: tabular-nums;
  pointer-events: none;
`;

export function TrackingPreviewStatusIndicator({
  statusLabel,
  framesSeen,
}: {
  readonly statusLabel: string;
  readonly framesSeen: number;
}) {
  return (
    <TrackingOverlay role="status" aria-live="polite" aria-atomic="true">
      <Spinner size="sm" />
      {statusLabel} · {framesSeen}
    </TrackingOverlay>
  );
}

/** 시간 구독을 fallback 비디오에 국한한다 — 프레임 리소스가 있으면 마운트조차 안 된다. */
function TrackingFallbackVideoSurface({
  transport,
  src,
  poster,
  crossOrigin,
  fallbackTimestampSeconds,
  onRasterChange,
}: {
  transport: PlaybackTransport;
  src: string;
  poster?: string | null;
  crossOrigin?: "anonymous" | "use-credentials";
  fallbackTimestampSeconds: number;
  onRasterChange: RasterReporter;
}) {
  const playing = useVideoTimeline(transport, (snapshot) => snapshot.playing);
  const currentTimeSeconds = useVideoTimeline(transport, (snapshot) => snapshot.currentTimeSeconds);
  const playbackRate = useVideoTimeline(transport, (snapshot) => snapshot.playbackRate);
  const setVideoRef = useVideoRaster(onRasterChange);
  return (
    <CapturedVideoSurface
      ref={setVideoRef}
      src={src}
      poster={poster}
      crossOrigin={crossOrigin}
      controls={false}
      showLabel={false}
      playing={playing}
      currentTimeSeconds={
        Number.isFinite(currentTimeSeconds) ? currentTimeSeconds : fallbackTimestampSeconds
      }
      playbackRate={playbackRate}
      timeUpdateIntervalMs={100}
      aria-label=""
    />
  );
}

export function TrackingPreviewViewport({
  controller,
  preview,
  playback,
  targets,
  mediaSrc,
  poster,
  crossOrigin,
  declaredAspectRatio,
  fallbackTimestampSeconds,
  label,
  statusLabel,
}: {
  controller: AnnotationStudioController;
  preview: AnnotationTrackingPreviewState;
  playback: TrackingPreviewPlayback;
  targets: readonly AnnotationTarget[];
  mediaSrc?: string;
  poster?: string | null;
  crossOrigin?: "anonymous" | "use-credentials";
  /** 소비처가 아는 media 비율 — raster의 실제 크기를 알기 전 스테이지 크기만 정한다. */
  declaredAspectRatio?: number | null;
  fallbackTimestampSeconds: number;
  label: string;
  statusLabel: string;
}) {
  const displayFrame = useTrackingPreviewDisplayFrame(playback);
  const frameResource = useTrackingFrameRenderResource(
    controller,
    displayFrame?.frameSourceRef ?? null,
  );
  const targetResults = Object.values(displayFrame?.targets ?? {});
  const stage = useAnnotationRasterStage({
    hasRaster: Boolean(frameResource || mediaSrc || poster),
    declaredAspectRatio,
  });
  const stageProps = annotationStageProps(stage);

  return (
    <AnnotationViewport
      role="img"
      aria-label={label}
      data-tracking-preview-viewport=""
      {...stageProps}
      style={{ ...stageProps.style, "--c6-annotation-cursor": "default" } as CSSProperties}
    >
      <Raster>
        {frameResource ? (
          <AnnotationRenderResourceImage
            image={frameResource.image}
            kind="tracking-preview"
            onRasterChange={stage.reportRaster}
          />
        ) : mediaSrc ? (
          <TrackingFallbackVideoSurface
            transport={playback.transport}
            src={mediaSrc}
            poster={poster}
            crossOrigin={crossOrigin}
            fallbackTimestampSeconds={fallbackTimestampSeconds}
            onRasterChange={stage.reportRaster}
          />
        ) : poster ? (
          <MeasuredImage src={poster} onRasterChange={stage.reportRaster} />
        ) : (
          <EmptyRaster />
        )}
        <AnnotationOverlay visible={stage.overlayVisible}>
          {targetResults.map((result) => {
            const target = targets.find((candidate) => candidate.id === result.targetId);
            return (
              <AnnotationContourLayer
                key={result.targetId}
                contours={result.contours}
                presentation="tracking"
                data-tracking-preview-target={result.targetId}
                style={
                  {
                    "--c6-annotation-target-color": target
                      ? targetColorCss(target.color)
                      : "currentColor",
                  } as CSSProperties
                }
              />
            );
          })}
        </AnnotationOverlay>
      </Raster>
      {playback.live ? (
        <ViewportOverlayActions>
          <TrackingPreviewStatusIndicator
            statusLabel={statusLabel}
            framesSeen={preview.framesSeen}
          />
        </ViewportOverlayActions>
      ) : null}
    </AnnotationViewport>
  );
}
